import React, {useCallback, useEffect, useMemo, useState} from "react";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import {Divider, Paper, Tooltip} from "@mui/material";
import SearchInput from "../../../../../components/base/input/search_input";
import {NotificationChannelTypes} from "../../../../../../core/constants/enums";
import Chips, {ChipsTypes} from "../../../../../components/base/chips/chips";
import {OutlinedDropdown} from "../../../../../components/base/dropdown/outlined_dropdown";
import {ReactComponent as UserIcon} from "../../../../../../assets/images/notification-channels/user.svg";
import {ReactComponent as OperatorIcon} from "../../../../../../assets/images/notification-channels/operator.svg";
import {ReactComponent as SystemIcon} from "../../../../../../assets/images/notification-channels/system.svg";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {NotificationChannelAccessLevelDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const dataGridSaveConfig = ({
    name: 'UserNotificationsAccessLevelsViewDataGrid',
    version: 1,
});

const UserNotificationsAccessLevelsView = () => {
    const [loading, setLoading] = useState(false);
    const [_channels, _setChannels] = useState([]);
    const [groups, setGroups] = useState([]);
    const [keywords, setKeywords] = useState('');
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('user-notifications-access-levels-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const filteredChannels = useMemo(() => {
        if (!keywords?.length) {
            return _channels;
        }
        return _channels.filter(e => e.description.toLowerCase().includes(keywords.toLowerCase().trim()) || keywords.toLowerCase().trim().includes(e.description.toLowerCase()));
    }, [keywords, _channels])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userNotificationsAccessLevels);
        configureBehavioralTool((tags) => tags.userNotificationsAccessLevels);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: NotificationChannelAccessLevelDataGridColumnNames.icon,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    width: 0,
                },
                {
                    title: 'Receive Notifications when',
                    name: NotificationChannelAccessLevelDataGridColumnNames.name,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 350,
                },
                {
                    title: 'Groups',
                    name: NotificationChannelAccessLevelDataGridColumnNames.groups,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        type: DataGridColumnWidthTypes.flex,
                        size: 1,
                        minWidth: 350,
                    },
                },
            ],
        [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Fetches the list of all channels and groups from the server and sets the inner state.
     * @type {function(): Promise<void>}
     */
    const getChannelsAndGroups = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllNotificationChannels();
        if (!isMounted()) return;
        const groups = response?.data?.allGroups ?? [];
        _setChannels(response?.data?.channels?.map(e => ({
            ...e,
            adding: false,
            groups: groups.filter(g => e?.operatorGroupIds?.includes(g.id))?.map(e => ({
                ...e,
                removing: false,
            })) ?? []
        })) ?? [])
        setGroups(groups)
        setLoading(false);
    }, [isMounted])

    /**
     * As soon as the component mounts:
     * - fetches the list of all channels and groups.
     */
    useEffect(() => {
        void getChannelsAndGroups();
    }, [getChannelsAndGroups])

    /**
     * Adds a group to a specific notification channel in the server.
     * @type {function(channel: *, group: Group): Promise<void>}
     */
    const addGroupToChannel = useCallback(async (channel, group) => {
        _setChannels(prevState => prevState?.map(e => e.id === channel.id
            ? {...e, adding: true}
            : e) ?? [])
        const forApi = {
            channelId: channel.id,
            groupId: group.id
        }
        const response = await Api.addGroupToNotificationChannel(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            _setChannels(prevState => prevState?.map(e => e.id === channel.id
                ? {
                    ...e,
                    groups: [
                        ...(e?.groups ?? []),
                        {...group, removing: false}
                    ],
                    operatorGroupIds: [...(e.operatorGroupIds ?? []), group.id]
                }
                : e) ?? [])
        }
        _setChannels(prevState => prevState?.map(e => e.id === channel.id
            ? {...e, adding: false}
            : e) ?? [])
    }, [isMounted])

    /**
     * Removes a group from a specific notification channel in the server.
     * @type {function(channel: *, group: Group): Promise<void>}
     */
    const removeGroupFromChannel = useCallback(async (channel, group) => {
        _setChannels(prevState => prevState?.map(e => e.id === channel.id
            ? {...e, groups: e?.groups?.map(e => e.id === group.id ? {...e, removing: true} : e) ?? []}
            : e) ?? [])
        const forApi = {
            channelId: channel.id,
            groupId: group.id
        }
        const response = await Api.removeGroupFromNotificationChannel(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            _setChannels(prevState => prevState?.map(e => e.id === channel.id
                ? {
                    ...e,
                    groups: e?.groups?.filter(e => e.id !== group.id) ?? [],
                    operatorGroupIds: e?.operatorGroupIds?.filter(e => e !== group.id) ?? []
                }
                : e) ?? [])
        } else {
            _setChannels(prevState => prevState?.map(e => e.id === channel.id
                ? {...e, groups: e?.groups?.map(e => e.id === group.id ? {...e, removing: false} : e) ?? []}
                : e) ?? [])
        }
    }, [isMounted])


    /**
     * Renders the icon associated with the given channel type.
     * @type {function(channel: *): JSX.Element}
     */
    const renderChannelIcon = useCallback((channel) => {
        switch (channel.type?.id) {
            case NotificationChannelTypes.user:
                return <>
                    <UserIcon/>
                </>
            case NotificationChannelTypes.operator:
                return <>
                    <OperatorIcon/>
                </>
            case NotificationChannelTypes.system:
                return <>
                    <SystemIcon/>
                </>
            case NotificationChannelTypes.userAndSystem:
                return <>
                    <UserIcon className={'mr-2'}/>
                    <SystemIcon/>
                </>
            case NotificationChannelTypes.operatorAndSystem:
                return <>
                    <OperatorIcon className={'mr-2'}/>
                    <SystemIcon/>
                </>
            default:
                return <></>;
        }
    }, [])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return filteredChannels?.map((channel) => {
            return ({
                key: channel?.id,
                data: channel,
                cells: {
                    [NotificationChannelAccessLevelDataGridColumnNames.icon]:
                        <Tooltip
                            placement={'top'}
                            title={channel?.type?.title}
                            disableInteractive>
                            <div className={'d-flex align-items-center'}>
                                {renderChannelIcon(channel)}
                            </div>
                        </Tooltip>,
                    [NotificationChannelAccessLevelDataGridColumnNames.name]: channel?.description,
                    [NotificationChannelAccessLevelDataGridColumnNames.groups]:
                        <Row className='no-gutters w-100 align-items-start justify-content-start flex-wrap'>
                            {
                                [
                                    ...channel?.groups?.map(group =>
                                        <div {...dataGridCellTextProps} key={`${group.id}`} className='mb-2 mt-1 mr-2'>
                                            <Chips
                                                disabled={group.removing}
                                                type={group.removing
                                                    ? ChipsTypes.loading
                                                    : ChipsTypes.remove
                                                }
                                                content={group.name}
                                                onIconClick={() => removeGroupFromChannel(channel, group)}
                                            />
                                        </div>) ?? [],
                                    <Col xs={6} md={4} key={`auto-complete-key`} className='mb-2'>
                                        <OutlinedDropdown
                                            label={'New Group'}
                                            data={groups
                                                ?.filter(e => !channel.operatorGroupIds?.includes(e.id))
                                                ?.map(group => ({
                                                    label: `${group.name}`,
                                                    id: group.id,
                                                    value: group,
                                                })) ?? []}
                                            value={''}
                                            disabled={channel?.adding}
                                            onChange={(g) => addGroupToChannel(channel, g)}
                                        />
                                    </Col>
                                ]
                            }
                        </Row>,
                },
            })
        })
    }, [addGroupToChannel, filteredChannels, groups, removeGroupFromChannel, renderChannelIcon])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-start align-items-center my-4'>
                <p className={'text-xl font-weight-semi-bold'}>
                    Notification Access Levels
                </p>
            </Row>
            <Paper className={`notification-access-levels-view paper w-100 mb-3`}>
                <Row className='no-gutters align-items-center justify-content-between w-100'>
                    <Col xs={12} className=''>
                        <SearchInput
                            loading={loading}
                            autoSearch={true}
                            search={setKeywords}
                            label={'Search'}
                            placeholder={'Search for notification channels'}
                        />
                    </Col>
                    <Divider className='divider'/>
                    <div className='w-100 my-2'/>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        rows={dataGridRows}
                        columns={dataGridColumns}
                        hideExporting
                        hideFooterActions
                        hideFooter
                        state={{
                            loading: {state: loading},
                            density: dataGridDensity,
                        }}
                    />
                </Row>
            </Paper>
        </>
    )
}

export default UserNotificationsAccessLevelsView;
